import { useStyles } from './styles/ListItem.styles';
import { Box, Divider, Collapse, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useState, memo, useCallback } from 'react';

import type { ComponentProps, MouseEventHandler, ReactNode } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

type BasicListItemProps = ComponentProps<typeof ListItemText> & {
    /**
     * Scroll the element into view on render.
     */
    focus?: boolean;
    /**
     * Shift the item to the left by a number of units.
     */
    left?: number;
    /**
     * Apply styles for a selected list item.
     */
    selected?: boolean;
    open?: boolean;
    disabled?: boolean;
    /**
     * Whether or not the item is expandable to display the provided `children`.
     */
    nesting?: boolean;
    children?: ReactNode;
};

const elementIsVisible = (elem: HTMLElement) => {
    const rect = elem.getBoundingClientRect();
    const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);

    return rect.bottom >= 0 && rect.top - viewHeight <= 0;
};

// Custom implementation of
// https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoViewIfNeeded
const scrollIntoViewIfNeeded = (elem: HTMLElement, ...rest: Parameters<HTMLElement['scrollIntoView']>) => {
    if (elementIsVisible(elem)) return;

    elem.scrollIntoView(...rest);
};

export const BasicListItem = memo(
    ({ focus, children, onClick, nesting, left = 0, selected, open, disabled, ...props }: BasicListItemProps) => {
        const classes = useStyles();

        return (
            <>
                <ListItemButton
                    disabled={disabled}
                    onClick={onClick}
                    className={`${nesting && open ? classes.open : ''} ${selected ? classes.selected : ''}`}>
                    <ListItemIcon sx={{ paddingLeft: left * 2 }}>
                        {!nesting ? (
                            <ArrowRightIcon className={classes.icon} />
                        ) : open ? (
                            <ArrowDropDownIcon className={classes.icon} />
                        ) : (
                            <ArrowDropUpIcon className={classes.icon} />
                        )}
                    </ListItemIcon>

                    <ListItemText
                        {...props}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}
                        primaryTypographyProps={{
                            color: '#696b6e',
                            fontSize: '15px',
                        }}
                    />
                </ListItemButton>

                {nesting && children && (
                    <Collapse in={open} unmountOnExit>
                        <Divider />
                        <Box
                            ref={
                                focus
                                    ? (elem?: HTMLDivElement) => {
                                          if (!elem || !selected) return;

                                          scrollIntoViewIfNeeded(elem, {
                                              behavior: 'smooth',
                                              block: 'nearest',
                                          });
                                      }
                                    : undefined
                            }>
                            {children}
                        </Box>
                    </Collapse>
                )}
            </>
        );
    }
);

type ListItemProps = Omit<BasicListItemProps, 'nesting' | 'onClick'> & {
    onClick: (open: boolean) => void;
};

/**
 * A {@link BasicListItem} that manages its own `open` state.
 */
export const ListItem = memo(({ open: openOverride, onClick, ...props }: ListItemProps) => {
    const [openState, setOpen] = useState(openOverride ?? false);

    // ? Need override?
    // const open = typeof openOverride === 'boolean' ? openOverride : openState;

    const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
        setOpen((prev) => {
            onClick?.(!prev);
            return !prev;
        });
    }, []);

    return <BasicListItem {...props} nesting={true} open={openState} onClick={handleClick} />;
});
